import classNames from "classnames";
import dayjs from "dayjs";
import { useState } from "react";
import { Button } from "../../../../components/buttons/button";
import { PopoverMenu } from "../../../../components/dropdown/popover";
import { CaretDown } from "../../../../components/icon/downcaret";
import { Icon } from "../../../../components/icon/icon";
import { FindEventsFilterModal } from "../../../../components/modal/app_modals/findEventsFilterModal";
import { TabNav } from "../../../../components/tabNav";
import { useDates } from "../../../../customeHooks/useDates";
import { useModalControl } from "../../../../customeHooks/useModalControl";
import { useMonthlyDates } from "../../../../customeHooks/useMonthlyDates";
import { useWeeklyDates } from "../../../../customeHooks/useWeeklyDates";
import { useWindowDimensions } from "../../../../customeHooks/useWindowDimensions";
import { FindEventsQuery } from "../../../../redux/features/event/event.types";
import { formatDate } from "../../../../utils/helpers";

const DateFilterComponent = ({
  query,
  setQuery,
  close,
}: {
  query: FindEventsQuery;
  setQuery: (q: FindEventsQuery) => void;
  close: () => void;
}) => {
  const [view, setView] = useState<"custom" | "default">("default");
  const [custView, setCustView] = useState<"months" | "weeks">("weeks");
  const [chosenDateRange, setChosenDateRange] = useState<{
    startDate?: string;
    endDate?: string;
    label?: string;
  }>({
    startDate: undefined,
    endDate: undefined,
    label: undefined,
  });

  const { tomorrow, saturday, sunday } = useDates();

  const {
    currentMonthRangeLabel,
    canGoBack,
    canGoNext,
    weekRanges,
    goToNextWeeks,
    goToPreviousWeeks,
  } = useWeeklyDates({
    initialDate: dayjs(),
  });

  const { months, setSelectedMonth } = useMonthlyDates({
    initialDate: dayjs(),
  });

  const defaultOptions = [
    {
      text: <p className="font-bold text-primary">Today</p>,
      onClick: () => {
        setQuery({
          ...query,
          page: 1,
          date: "today",
          startDate: undefined,
          endDate: undefined,
          search: undefined,
        });
      },
      closeOnClick: true,
    },
    {
      text: (
        <p className="flex flex-col text-sm">
          <span className="font-bold text-primary">Tomorrow</span>
          <span className="text-grey-500">
            {formatDate(tomorrow, "MMM Do")}
          </span>
        </p>
      ),
      onClick: () => {
        setQuery({
          ...query,
          page: 1,
          date: "tomorrow",
          startDate: undefined,
          endDate: undefined,
          search: undefined,
        });
      },
      closeOnClick: true,
    },
    {
      text: (
        <p className="flex flex-col text-sm">
          <span className="font-bold text-primary">This Weekend</span>
          <span className="text-grey-500">
            {formatDate(saturday, "MMM Do")} - {formatDate(sunday, "MMM Do")}
          </span>
        </p>
      ),
      onClick: () => {
        setQuery({
          ...query,
          page: 1,
          date: "this-weekend",
          startDate: undefined,
          endDate: undefined,
          search: undefined,
        });
      },
      closeOnClick: true,
    },
    {
      text: <p className="font-bold text-primary">Choose Date</p>,
      onClick: () => {
        setView("custom");
      },
      closeOnClick: false,
    },
  ];

  return (
    <div
      className={classNames(
        "md:rounded-xl md:border md:border-grey-200 md:!bg-white",
        "md:h-full md:overflow-y-scroll",
        {
          "md:flex md:w-[177px] md:flex-col": view === "default",
          "md:!max-h-fit md:w-[300px]": view === "custom",
        },
      )}
    >
      {view === "default" ? (
        <>
          {defaultOptions.map((option, idx) => {
            return (
              <button
                type="button"
                onClick={() => {
                  option.onClick();
                  if (option.closeOnClick) {
                    close();
                  }
                }}
                className="w-full border-b border-b-grey-200 p-4 text-left last:border-b-0"
                key={`option-${idx}`}
              >
                {option.text}
              </button>
            );
          })}
        </>
      ) : (
        <>
          <div className="px-3 py-4">
            <TabNav
              activeTab={custView}
              navs={[
                {
                  title: "Weeks",
                  to: "weeks",
                  onClick: () => {
                    setCustView("weeks");
                  },
                },
                {
                  title: "Months",
                  to: "months",
                  onClick: () => {
                    setCustView("months");
                  },
                },
              ]}
              variant="switcher"
            />
          </div>

          <div className="flex items-center border-y border-grey-200 p-3">
            <Icon
              svg="caret-down"
              width={20}
              height={20}
              className={classNames("rotate-90", {
                "cursor-not-allowed text-grey-200": !canGoBack,
                "cursor-pointer text-grey-500": canGoBack,
                hidden: custView === "months",
              })}
              onClick={() => {
                goToPreviousWeeks();
              }}
            />

            <p className="flex-1 text-center text-base font-bold text-primary">
              {custView === "weeks" ? currentMonthRangeLabel : dayjs().year()}
            </p>

            <Icon
              svg="caret-down"
              width={20}
              height={20}
              className={classNames("-rotate-90", {
                "cursor-not-allowed text-grey-200": !canGoNext,
                "cursor-pointer text-grey-500": canGoNext,
                hidden: custView === "months",
              })}
              onClick={() => {
                goToNextWeeks();
              }}
            />
          </div>

          {custView === "weeks" ? (
            <div className="flex flex-col">
              {weekRanges.map((wRange, idx) => {
                return (
                  <button
                    type="button"
                    className={classNames(
                      "px-5 py-3 text-left text-base font-bold",
                      {
                        "text-primary": !wRange.disabled,
                        "cursor-not-allowed text-grey-300": wRange.disabled,
                        "bg-grey-100": wRange.label === chosenDateRange.label,
                      },
                    )}
                    onClick={() => {
                      setChosenDateRange({
                        startDate: wRange.start.format("YYYY-MM-DD"),
                        endDate: wRange.end.format("YYYY-MM-DD"),
                        label: wRange.label,
                      });
                      setSelectedMonth(null);
                    }}
                    disabled={wRange.disabled}
                    key={`week-range-${idx}`}
                  >
                    {wRange.label}
                  </button>
                );
              })}
            </div>
          ) : (
            <div className="grid grid-cols-4 gap-y-6 py-3">
              {months.map((month, idx) => {
                return (
                  <button
                    type="button"
                    className={classNames(
                      "w-full rounded-[4px] p-2 text-base font-bold",
                      {
                        "text-primary": !month.disabled,
                        "cursor-not-allowed text-grey-300": month.disabled,
                        "bg-grey-100": month.label === chosenDateRange.label,
                      },
                    )}
                    onClick={() => {
                      setChosenDateRange({
                        startDate: month.start.format("YYYY-MM-DD"),
                        endDate: month.end.format("YYYY-MM-DD"),
                        label: month.label,
                      });
                      setSelectedMonth(month);
                    }}
                    disabled={month.disabled}
                    key={`week-range-${idx}`}
                  >
                    {month.label}
                  </button>
                );
              })}
            </div>
          )}

          <div className="border-t border-grey-200 pt-3 md:p-3">
            <Button
              type="button"
              variation="primary"
              text="Apply"
              onClick={() => {
                setQuery({
                  ...query,
                  page: 1,
                  date: undefined,
                  startDate: chosenDateRange.startDate,
                  endDate: chosenDateRange.endDate,
                  search: undefined,
                });
                close();
              }}
            />
          </div>
        </>
      )}
    </div>
  );
};

export const DateFilter = ({
  query,
  setQuery,
}: {
  query: FindEventsQuery;
  setQuery: (q: FindEventsQuery) => void;
}) => {
  const { handleModalClose, handleModalOpen, modal, modalOpen } =
    useModalControl();

  const { width } = useWindowDimensions();

  return (
    <>
      <PopoverMenu
        className="min-w-max"
        customMenuItems={({ close }) => {
          return (
            <DateFilterComponent
              query={query}
              setQuery={setQuery}
              close={() => {
                close();
              }}
            />
          );
        }}
        menuClassName="!min-w-fit !hidden md:!flex"
        position="bottom start"
      >
        {({ open }) => (
          <Button
            type="button"
            text={
              query.startDate && query.endDate
                ? `${formatDate(query?.startDate, "MMM Do", true)} - ${formatDate(query?.endDate, "MMM Do", true)}`
                : query.date
                  ? query?.date?.replace("-", " ")
                  : `Date`
            }
            variation="bland"
            size="zero"
            extra={
              (query.startDate && query.endDate) || query.date ? (
                <p
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setQuery({
                      ...query,
                      date: undefined,
                      startDate: undefined,
                      endDate: undefined,
                      page: 1,
                    });
                  }}
                >
                  <Icon
                    svg="close-icon"
                    width={20}
                    height={20}
                    className="text-grey-600"
                  />{" "}
                </p>
              ) : (
                <CaretDown width={20} height={20} />
              )
            }
            className={`!w-fit !justify-start gap-[2px] font-onest !text-sm !font-medium capitalize !text-ink tracking-[-0.42px] ${open ? "opacity-70" : ""}`}
            onClick={
              width < 768
                ? () => {
                    handleModalOpen("dateFilterModal");
                  }
                : undefined
            }
          />
        )}
      </PopoverMenu>

      <FindEventsFilterModal
        open={modalOpen && modal === "dateFilterModal"}
        handleClose={() => {
          handleModalClose();
        }}
        title="Filter by date"
      >
        <DateFilterComponent
          query={query}
          setQuery={setQuery}
          close={() => {
            handleModalClose();
          }}
        />
      </FindEventsFilterModal>
    </>
  );
};
